import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ExternalLink, ChevronDown, ChevronRight, RefreshCw, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import EditableText from "./EditableText";
import EditableLabel from "./EditableLabel";
import { useReorder } from "../lib/useReorder";
import { useEditMode } from "../lib/EditModeContext";

/**
 * Top 3 — live from Todoist (@Top_3 label).
 * Everything editable: double-click content to push to Todoist, × to close,
 * drag handle (edit-mode) to reorder locally.
 */
const Top3 = () => {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dueToday, setDueToday] = useState(null);
  const { editMode } = useEditMode();

  const load = async () => {
    setLoading(true);
    try {
      const [list, dt] = await Promise.all([api.top3(), api.dueTodayCount()]);
      setItems(list || []);
      setDueToday(dt?.count ?? null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const onSync = () => load();
    window.addEventListener("operator:sync", onSync);
    return () => window.removeEventListener("operator:sync", onSync);
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
    toast("Refreshed from Todoist");
  };

  const { rowProps, handleProps, dragId, overId } = useReorder(items, async (newIds) => {
    setItems((prev) => newIds.map((id) => prev.find((p) => p.id === id)).filter(Boolean));
    try { await api.reorderTop3(newIds); } catch { load(); }
  });

  const editParent = async (t, newContent) => {
    setItems((prev) => prev.map((c) => c.id === t.id ? { ...c, content: newContent } : c));
    try { await api.updateTodoistTask(t.id, newContent); toast("Updated in Todoist"); }
    catch { toast("Todoist update failed"); load(); }
  };

  const editSub = async (parent, sub, newContent) => {
    setItems((prev) => prev.map((c) => c.id === parent.id ? {
      ...c,
      subtasks: c.subtasks.map((s) => s.id === sub.id ? { ...s, content: newContent } : s),
    } : c));
    try { await api.updateTodoistTask(sub.id, newContent); toast("Updated in Todoist"); }
    catch { toast("Todoist update failed"); load(); }
  };

  const deleteParent = async (t) => {
    const snapshot = t;
    setItems((prev) => prev.filter((c) => c.id !== t.id));
    try {
      await api.closeTodoistTask(t.id);
      toast("Closed in Todoist", {
        action: {
          label: "UNDO",
          onClick: async () => {
            try { await api.reopenTodoistTask(t.id); toast("Reopened"); load(); }
            catch { toast("Undo failed"); }
          },
        },
        duration: 6000,
      });
    } catch { toast("Todoist close failed"); load(); }
  };

  const deleteSub = async (parent, sub) => {
    setItems((prev) => prev.map((c) => c.id === parent.id ? {
      ...c,
      subtasks: c.subtasks.filter((s) => s.id !== sub.id),
      progress: {
        done: c.subtasks.filter((s) => s.id !== sub.id && s.completed).length,
        total: Math.max(0, c.subtasks.length - 1),
      },
    } : c));
    try {
      await api.closeTodoistTask(sub.id);
      toast("Subtask closed", {
        action: {
          label: "UNDO",
          onClick: async () => {
            try { await api.reopenTodoistTask(sub.id); toast("Reopened"); load(); }
            catch { toast("Undo failed"); }
          },
        },
        duration: 6000,
      });
    } catch { toast("Todoist close failed"); load(); }
  };

  const toggleSub = async (parent, sub) => {
    setItems((prev) => prev.map((c) => c.id === parent.id ? {
      ...c,
      subtasks: c.subtasks.map((s) => s.id === sub.id ? { ...s, completed: !s.completed } : s),
      progress: {
        done: c.subtasks.filter((s) => s.id === sub.id ? !s.completed : s.completed).length,
        total: c.subtasks.length,
      },
    } : c));
    try {
      if (!sub.completed) await api.closeTodoistTask(sub.id);
      else await api.reopenTodoistTask(sub.id);
    } catch { toast("Todoist sync failed"); load(); }
  };

  return (
    <section className="relative" data-testid="top3-section">
      <SectionHeader
        code="01" label="TODAY'S TOP 3"
        caption="Live from Todoist · @Top_3 label"
        labelKey="section.top3.label"
        captionKey="section.top3.caption"
        right={
          <div className="flex items-center gap-3">
            {dueToday !== null && (
              <span className="op-mono text-[10px] tracking-[0.24em] text-[#B4BAD1]" data-testid="due-today-count">
                DUE TODAY · <span className="text-[#4A9EFF]">{dueToday}</span>
              </span>
            )}
            <button
              onClick={refresh}
              data-testid="top3-refresh"
              className="op-btn text-[10px] px-3 py-1.5"
              title="Force fresh Todoist sync"
            >
              <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
              <span className="ml-1.5">REFRESH</span>
            </button>
          </div>
        }
      />

      {loading && items.length === 0 && (
        <div className="op-panel p-8 text-center text-[#6A7396] op-mono text-[11px] tracking-[0.24em]" data-testid="top3-loading">
          LOADING FROM TODOIST…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="op-panel p-8 text-center text-[#6A7396]" data-testid="top3-empty">
          <div className="op-mono text-[11px] tracking-[0.24em] mb-2">NO @TOP_3 TASKS</div>
          <div className="text-sm">Add the <span className="text-[#4A9EFF]">@Top_3</span> label to 3 tasks in Todoist.</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {items.map((t, idx) => {
          const isOpen = expanded[t.id] ?? true;
          return (
            <div
              key={t.id}
              data-testid={`top3-card-${idx + 1}`}
              {...rowProps(t.id)}
              data-dragging={dragId === t.id}
              data-drop-active={overId === t.id}
              className="op-panel op-editable-row p-6 overflow-hidden min-h-[240px] relative flex flex-col group"
            >
              <div className="op-ghost-number text-[120px] absolute -right-2 -top-8" aria-hidden="true">
                {String(idx + 1).padStart(2, "0")}
              </div>

              {/* Top-right actions: drag handle + delete (visible on hover / edit mode) */}
              <div className="absolute right-3 top-3 z-20 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ opacity: editMode ? 1 : undefined }}>
                {editMode && (
                  <span
                    {...handleProps(t.id)}
                    className="op-row-handle"
                    data-testid={`top3-drag-${idx + 1}`}
                    aria-label="Drag to reorder"
                  >
                    <GripVertical size={12} strokeWidth={1.6} />
                  </span>
                )}
                <button
                  onClick={() => deleteParent(t)}
                  className="op-row-delete"
                  data-testid={`top3-delete-${idx + 1}`}
                  aria-label="Close in Todoist"
                  title="Close in Todoist"
                >
                  <X size={12} strokeWidth={2} />
                </button>
              </div>

              <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <span className="op-tag" data-testid={`top3-business-${idx + 1}`}>
                    {t.business || t.project_name || "TODOIST"}
                  </span>
                  {t.todoist_url && (
                    <a
                      href={t.todoist_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#6BC5FF]/70 hover:text-[#6BC5FF]"
                      title="Open in Todoist"
                    >
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>

                <EditableText
                  as="h3"
                  value={t.content}
                  onSave={(v) => editParent(t, v)}
                  multiline
                  className="text-white text-xl md:text-2xl font-medium leading-tight tracking-tight mb-4 block"
                  testid={`top3-content-${idx + 1}`}
                />

                {t.subtasks.length > 0 && (
                  <div className="mt-auto">
                    <button
                      onClick={() => setExpanded((e) => ({ ...e, [t.id]: !isOpen }))}
                      data-testid={`top3-toggle-${idx + 1}`}
                      className="flex items-center gap-2 op-mono text-[10px] tracking-[0.24em] text-[#B4BAD1] hover:text-white mb-3"
                    >
                      {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      <span data-testid={`top3-progress-${idx + 1}`}>
                        {t.progress.done} OF {t.progress.total} COMPLETED
                      </span>
                    </button>
                    {isOpen && (
                      <ul className="space-y-2 mb-4" data-testid={`top3-subtasks-${idx + 1}`}>
                        {t.subtasks.map((s) => (
                          <li key={s.id} className="flex items-start gap-2.5 group/sub">
                            <button
                              onClick={() => toggleSub(t, s)}
                              data-testid={`top3-sub-check-${s.id}`}
                              className="op-check mt-0.5"
                              data-checked={s.completed}
                              aria-label="toggle subtask"
                            />
                            <EditableText
                              value={s.content}
                              onSave={(v) => editSub(t, s, v)}
                              className={`text-sm leading-snug flex-1 ${
                                s.completed ? "text-[#6A7396] line-through" : "text-white/90"
                              }`}
                              testid={`top3-sub-content-${s.id}`}
                            />
                            <button
                              onClick={() => deleteSub(t, s)}
                              className="op-row-delete opacity-0 group-hover/sub:opacity-100 transition-opacity"
                              data-testid={`top3-sub-delete-${s.id}`}
                              aria-label="Close subtask"
                              title="Close subtask"
                              style={{ opacity: editMode ? 1 : undefined }}
                            >
                              <X size={10} strokeWidth={2} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="mt-auto pt-3">
                  <button
                    onClick={() => deleteParent(t)}
                    data-testid={`top3-complete-${idx + 1}`}
                    className="op-btn op-btn--primary text-[10px] px-4 py-2 w-full"
                  >
                    MARK COMPLETE
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const SectionHeader = ({ code, label, caption, labelKey, captionKey, tagKey, tagText = "EXECUTION", right }) => (
  <div className="mb-6 flex items-end justify-between gap-6">
    <div>
      <div className="flex items-center gap-3 mb-2">
        <span className="op-mono text-[10px] tracking-[0.28em] text-[#6A7396]">{code}</span>
        <span className="op-hairline w-16" />
        <span className="op-mono text-[10px] tracking-[0.28em] text-[#4A9EFF]">
          {tagKey ? <EditableLabel labelKey={tagKey} defaultText={tagText} /> : tagText}
        </span>
      </div>
      <h2 className="text-white text-2xl md:text-3xl font-normal tracking-tight">
        {labelKey ? <EditableLabel labelKey={labelKey} defaultText={label} /> : label}
      </h2>
      {(caption || captionKey) && (
        <p className="text-[#B4BAD1] text-sm mt-1">
          {captionKey ? <EditableLabel labelKey={captionKey} defaultText={caption || ""} /> : caption}
        </p>
      )}
    </div>
    {right}
  </div>
);

export default Top3;
export { SectionHeader };
